import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { roundToDecimalPlaces } from '@semios/app-platform-common'
import { getEmitterTypeToEnum } from '@semios/app-platform-values-helpers-and-types'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import type {
  TIrrigationDeviceStatus,
  TValuesCurrentIrrigationZones,
  TValuesCurrentProperties,
} from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { isEmpty, isNil } from 'lodash'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { smallStore } from 'stores/smallStore/smallStore'
import { apiFetch } from 'utils/apiFetch'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { TIrrigationActivityForPointOrZone } from '../../caches/StationValuesCache/StationValuesCache'
import type { TGetCacheUpdatesFromResponseParameters, TGetCacheUpdatesFromResponseReturn } from './_types'
import { createValuesMap } from './_utils/createValuesMap'
import { getHeatmapCSS } from './_utils/getHeatmapCSS'
import { getIrrigationStatusReturn } from './_utils/getIrrigationStatusReturn'
import { getStatusValue } from './_utils/getStatusValue'
import { getValueType } from './_utils/getValueType'
import { insertEmitterType } from './_utils/insertEmitterType'
import { insertEmitterTypeForStatus } from './_utils/insertEmitterTypeForStatus'
import {
  IRRIGATION_ZONE_ACTIVITY_APPLIED_EVENT_DURATION_VALUE_TYPE_PREFIX,
  IRRIGATION_ZONE_ACTIVITY_PUBLISHED_EVENT_DURATION_VALUE_TYPE_PREFIX,
  MILLISECONDS_IN_HOUR,
  STATUS_VALUE_TYPE,
  Z_INDEX_HAS_DATA,
  Z_INDEX_NO_DATA,
  Z_INDEX_NOT_APPLICABLE,
  Z_INDEX_OFF,
} from './_utils/irrigationActivityConstants'
import { setHeatmapExtremesFromArrayOfValues } from './_utils/setHeatmapExtremesFromArrayOfValues'

const makeApiArgs = (
  processedCaches: TGetCacheUpdatesFromResponseParameters['processedCaches'],
): routes.ValuesCurrent.Request => {
  const selectedEmitters = mapControlsStore.getState().selectedEmitters
  const irrigationZoneValueType = getValueType(MAP_VISUAL.IRRIGATION_ZONE, 'irrigation_activity_zones')
  const propertyValueType = getValueType(MAP_VISUAL.PROPERTY, 'irrigation_activity_zones')
  const returner: routes.ValuesCurrent.Request = {}

  const valueTypes = selectedEmitters.flatMap((emitter) => [
    insertEmitterType(irrigationZoneValueType, emitter),
    insertEmitterTypeForStatus(emitter),
  ])

  const propertyValueTypes = selectedEmitters.flatMap((emitter) => [
    insertEmitterType(propertyValueType, emitter) as TValuesCurrentProperties,
    insertEmitterTypeForStatus(emitter) as TValuesCurrentProperties,
  ])

  const propertyItems = processedCaches.propertyValues.itemsWithinView
  const zoneItems = processedCaches.irrigationZoneValues.itemsWithinView

  if (zoneItems.length) {
    const irrigationZoneIds = [...new Set(zoneItems.map((zone) => zone.meta.irrigationZoneId))]

    returner.irrigation_zones = {
      irrigationZoneIds,
      values: createValuesMap(valueTypes),
    }

    if (irrigationZoneValueType === STATUS_VALUE_TYPE) {
      selectedEmitters.forEach((emitter) => {
        const appliedValueType =
          `${IRRIGATION_ZONE_ACTIVITY_APPLIED_EVENT_DURATION_VALUE_TYPE_PREFIX}${getEmitterTypeToEnum(
            emitter,
          )}_ms` as const

        const publishedValueType =
          `${IRRIGATION_ZONE_ACTIVITY_PUBLISHED_EVENT_DURATION_VALUE_TYPE_PREFIX}${getEmitterTypeToEnum(
            emitter,
          )}_ms` as const

        if (returner.irrigation_zones) {
          returner.irrigation_zones.values[appliedValueType] = true

          returner.irrigation_zones.values[publishedValueType] = true
        }
      })
    }
  }

  if (propertyItems.length !== 0) {
    returner.properties = {
      propertyIds: propertyItems.map((property) => Number(property.id)),
      values: createValuesMap(propertyValueTypes),
    }
  }

  return returner
}

export const getResponseAndShapeForCacheUpdate = async ({
  cacheKeys,
  processedCaches,
}: TGetCacheUpdatesFromResponseParameters): Promise<TGetCacheUpdatesFromResponseReturn> => {
  const args = makeApiArgs(processedCaches)

  if (isEmpty(args)) return {}

  const response = await apiFetch({ url: '/values-current', body: args })
  const selectedEmitters = mapControlsStore.getState().selectedEmitters
  const propertyItems = processedCaches.propertyValues.itemsWithinView
  const zoneItems = processedCaches.irrigationZoneValues.itemsWithinView
  const irrigationZoneValueType = getValueType(MAP_VISUAL.IRRIGATION_ZONE, 'irrigation_activity_zones')
  const propertyValueType = getValueType(MAP_VISUAL.PROPERTY, 'irrigation_activity_zones')
  const returner: TGetCacheUpdatesFromResponseReturn = {}
  const valueTypeIsForStatus = irrigationZoneValueType === STATUS_VALUE_TYPE
  const appliedIrrigationActivityDisplay = smallStore.getState().appliedIrrigationActivityDisplay
  const allValuesForHeatmap: number[] = []
  const properties = fieldAssetStore.getState().properties ?? {}

  if (zoneItems.length) {
    const itemIdsWithinView: string[] = []

    const itemsWithinViewThatNowHaveValues = zoneItems.flatMap((iZone) => {
      const irrigationZoneId = iZone.meta.irrigationZoneId
      const emittersOnZone = properties[Number(iZone.meta.propertyId)]?.irrigationZoneEmitters
      const emittersOnZoneKeys = Object.keys(emittersOnZone ?? {}) as TIrrigationZoneEmitterId[]

      const filteredEmitterIdsByZone = emittersOnZoneKeys.filter((z) => {
        const { irrigationZoneId: zoneId, emitterType } = getIdAndEmitterTypeFromZoneEmitterTypeKey(z)

        return selectedEmitters.includes(emitterType) && Number(irrigationZoneId) === Number(zoneId)
      }) as TIrrigationZoneEmitterId[]

      itemIdsWithinView.push(iZone.id)

      const irrigationZoneValuesFromResponse = response.irrigation_zones?.[irrigationZoneId] ?? {}

      const irrigationEmittersWithFlowRatesForZone =
        filteredEmitterIdsByZone.reduce<TIrrigationActivityForPointOrZone>((acc, id) => {
          const { emitterType } = getIdAndEmitterTypeFromZoneEmitterTypeKey(id)

          const idAsValueType = insertEmitterType(
            irrigationZoneValueType,
            emitterType,
          ) as TValuesCurrentIrrigationZones

          const emitterStatusValueType = insertEmitterTypeForStatus(
            emitterType,
          ) as TValuesCurrentIrrigationZones

          const responsePoint = irrigationZoneValuesFromResponse[idAsValueType]
          const irrigationZoneValueFromResponse = responsePoint?.value

          const duration = isNaN(Number(irrigationZoneValueFromResponse))
            ? null
            : Number(irrigationZoneValueFromResponse)

          const flowRate = emittersOnZone?.[id]?.flowRate
          const flowRateMMHr = flowRate?.mmPerHour

          const status = (irrigationZoneValuesFromResponse[emitterStatusValueType]?.value ??
            'unknown') as TIrrigationDeviceStatus

          const applied = duration && flowRateMMHr ? (duration / MILLISECONDS_IN_HOUR) * flowRateMMHr : null
          const getBadgeColors = () => getHeatmapCSS({ value: applied })

          acc[id] = {
            getBadgeColors,
            duration,
            applied,
            flowRate,
            status,
          }

          return acc
        }, {})

      if (valueTypeIsForStatus) {
        const statusValue = getStatusValue({
          irrigationZoneValueType,
          id: iZone.id,
          irrigationZoneValuesFromResponse,
        })

        return {
          ...statusValue,
          value: {
            ...statusValue.value,
            [irrigationZoneValueType]: {
              ...statusValue.value[irrigationZoneValueType],
              valuesForZone: irrigationEmittersWithFlowRatesForZone,
            },
          },
        }
      }

      if (appliedIrrigationActivityDisplay === 'DURATION') {
        const aggregatedDuration = Object.values(irrigationEmittersWithFlowRatesForZone ?? {}).reduce<number>(
          (acc, curr) => acc + (curr.duration ?? 0),
          0,
        )

        const durationHours = Number(aggregatedDuration) / MILLISECONDS_IN_HOUR
        const durationHoursRounded = Number(roundToDecimalPlaces(durationHours, 1))

        allValuesForHeatmap.push(durationHoursRounded)

        return {
          id: String(iZone.id),
          value: {
            [irrigationZoneValueType]: {
              // nice to make non-zero amounts pop out more
              baseZIndex: durationHours > 0 ? Z_INDEX_HAS_DATA : Z_INDEX_OFF,
              children: durationHoursRounded,
              getContainerCSS: () => getHeatmapCSS({ value: durationHours }),
              valuesForZone: irrigationEmittersWithFlowRatesForZone,
            },
          },
        }
        // VOLUME
      } else {
        const appliedWaterMM = Object.values(irrigationEmittersWithFlowRatesForZone ?? {}).reduce<number>(
          (acc, curr) => {
            return acc + (curr.applied ?? 0)
          },
          0,
        )

        const appliedAmountFinalUnit = unitConverter.irrigationActivity(appliedWaterMM).value()

        if (appliedAmountFinalUnit) allValuesForHeatmap.push(appliedAmountFinalUnit)

        return {
          id: String(iZone.id),
          value: {
            [irrigationZoneValueType]: {
              children: !!appliedWaterMM ? appliedAmountFinalUnit : 0,
              getContainerCSS: () => getHeatmapCSS({ value: appliedAmountFinalUnit }),
              baseZIndex: appliedWaterMM > 0 ? Z_INDEX_HAS_DATA : Z_INDEX_OFF,
              valuesForZone: irrigationEmittersWithFlowRatesForZone,
            },
          },
        }
      }
    })

    returner.irrigationZones = {
      itemsWithinViewThatNowHaveValues,
      cacheKey: cacheKeys.irrigationZoneCacheKey,
      itemIdsWithinView,
    }
  }

  const showValuesForPropertyRatherThanName =
    propertyValueType &&
    !zoneItems.length &&
    (appliedIrrigationActivityDisplay === 'DURATION' || valueTypeIsForStatus)

  const itemsWithinViewThatNowHaveValues = propertyItems.map((property) => {
    if (!showValuesForPropertyRatherThanName) {
      return {
        id: String(property.id),
        value: {
          [propertyValueType]: {
            baseZIndex: Z_INDEX_NOT_APPLICABLE,
            children: property.meta.propertyName,
            getContainerCSS: () => ({ backgroundColor: colors.midnight, color: colors.white }),
            onHover: false,
          },
        },
      }
    }

    const propertyFromResponse = response.properties?.[property.id] ?? {}

    const irrigationZoneEmitterIds = Object.keys(
      properties[Number(property.id)]?.irrigationZoneEmitters ?? {},
    ) as TIrrigationZoneEmitterId[]

    const matchingZoneEmitterIdsOnProperty = irrigationZoneEmitterIds.filter((z) => {
      const { emitterType } = getIdAndEmitterTypeFromZoneEmitterTypeKey(z)

      return selectedEmitters.includes(emitterType)
    })

    /**
     * if the selected value type is for an applied amount, then it is
     * for a specific emitter type, but if the property doesn't have
     * that emitter type, we should just show "N/A" for the property.
     * We can determine if the emitter is present for the property by
     * checking the property from the fieldAssetStore's properties
     * object
     */
    const propertyPressureTransducers = Object.values(properties?.[Number(property.id)]?.points || {}).filter(
      (p) => !isEmpty(p.configuration.irrigationEmitterTypesAvailable),
    )

    const propertyHasChosenEmitterAndEmitterIsSelected = selectedEmitters.some((emitter) => {
      return (
        propertyPressureTransducers?.some((p) =>
          p.configuration.irrigationEmitterTypesAvailable?.includes(emitter),
        ) && matchingZoneEmitterIdsOnProperty.some((z) => z.includes(emitter))
      )
    })

    if (isEmpty(propertyPressureTransducers) || !propertyHasChosenEmitterAndEmitterIsSelected) {
      return {
        id: String(property.id),
        value: {
          [propertyValueType]: {
            children: translate.phrases.abbreviations('Not Applicable'),
            getContainerCSS: () => ({ backgroundColor: colors.grey200, color: colors.midnight }),
            baseZIndex: Z_INDEX_NOT_APPLICABLE,
            onHover: true,
          },
        },
      }
    }

    const nonStatusValueTypes = Object.keys(propertyFromResponse).filter((key) => {
      return !key.startsWith(STATUS_VALUE_TYPE)
    })

    const aggregatedDuration = nonStatusValueTypes.reduce<number>((acc, id) => {
      const propertyValue = propertyFromResponse[id as TValuesCurrentProperties]

      if (propertyValue && 'max_value' in propertyValue) {
        acc += propertyValue.max_value ?? 0
      }

      return acc
    }, 0)

    const value = valueTypeIsForStatus ? propertyFromResponse[STATUS_VALUE_TYPE]?.value : aggregatedDuration

    if (!isNil(value)) {
      if (valueTypeIsForStatus) {
        return {
          id: String(property.id),
          value: {
            [propertyValueType]: {
              ...getIrrigationStatusReturn(value as TIrrigationDeviceStatus),
              onHover: true,
            },
          },
        }
      }

      const valueAsNumber = Number(value)
      const durationHours = valueAsNumber / MILLISECONDS_IN_HOUR
      const durationHoursRounded = Number(roundToDecimalPlaces(durationHours, 1))
      // note that we will never show applied amounts at the property level, just duration

      allValuesForHeatmap.push(durationHoursRounded)

      return {
        id: String(property.id),
        value: {
          [propertyValueType]: {
            // nice to make non-zero amounts pop out more
            baseZIndex: durationHours > 0 ? Z_INDEX_HAS_DATA : Z_INDEX_OFF,
            children: durationHoursRounded,
            getContainerCSS: () => getHeatmapCSS({ value: durationHours }),
            onHover: true,
          },
        },
      }
    }

    return {
      id: String(property.id),
      value: {
        [propertyValueType]: {
          children: translate.phrases.templates('-'),
          getContainerCSS: () => ({ backgroundColor: colors.grey200, color: colors.midnight }),
          baseZIndex: Z_INDEX_NO_DATA,
          onHover: true,
        },
      },
    }
  })

  setHeatmapExtremesFromArrayOfValues({
    heatmapColoring: [
      colors.irrigationHeatmapSoftBlue,
      colors.irrigationHeatmapMediumBlue,
      colors.irrigationHeatmapStrongBlue,
    ],
    values: allValuesForHeatmap,
  })

  returner.properties = {
    itemsWithinViewThatNowHaveValues,
    cacheKey: cacheKeys.propertyCacheKey,
    itemIdsWithinView: processedCaches.propertyValues.itemIdsWithinView,
  }

  return returner
}
